import type { ConnectError } from '@khal-os/sdk/app';
import type { ReactElement } from 'react';
import { OriginRejectedScreen } from './OriginRejectedScreen';
import { UnauthenticatedScreen } from './UnauthenticatedScreen';
import { VersionMismatchScreen } from './VersionMismatchScreen';

export interface ConnectingScreenProps {
	/** The last ConnectError observed by the retry loop. */
	error: ConnectError;
	/** Kernel hostname shown on copy (best-effort; host app typically knows this). */
	kernel?: string;
	/** Optional admin contact URL for origin-rejected cases. */
	contactUrl?: string;
	/** Escape hatch used by every terminal branch. */
	onBackToLauncher?: () => void;
	/**
	 * Fallback renderer for `server-closed(transient=false)` and any future
	 * kind the shell doesn't know about. Kept injectable so the host app can
	 * wire its existing `ConnectionErrorScreen` here.
	 */
	renderFallback: (err: ConnectError) => ReactElement;
}

/**
 * Router that inspects the typed `ConnectError` and renders the right CTA.
 * Extracted so the retry loop can drop a single `<ConnectingScreen/>` into
 * the tree and be sure the UI will match the failure mode — no silent
 * "reconnecting" spinners on fatal errors.
 */
export function ConnectingScreen({ error, kernel, contactUrl, onBackToLauncher, renderFallback }: ConnectingScreenProps) {
	switch (error.kind) {
		case 'unauthenticated':
			return <UnauthenticatedScreen signInUrl={error.signInUrl} onBackToLauncher={onBackToLauncher} />;
		case 'token-expired':
			return <UnauthenticatedScreen signInUrl={error.signInUrl} expired onBackToLauncher={onBackToLauncher} />;
		case 'origin-rejected':
			return (
				<OriginRejectedScreen
					email={error.email}
					kernel={kernel}
					reason={error.reason}
					contactUrl={contactUrl}
					onBackToLauncher={onBackToLauncher}
				/>
			);
		case 'version-mismatch':
			return (
				<VersionMismatchScreen
					serverVersion={error.server}
					clientVersion={error.client}
					onBackToLauncher={onBackToLauncher}
				/>
			);
		default:
			return renderFallback(error);
	}
}
